import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { TrashIcon } from '@heroicons/react/outline';
import { convertToMinutes, convertFromMinutes, getTriggerOptions } from '../utils/triggerOptions';
import clsx from 'clsx';

const CampaignSequenceForm = ({ 
	sequence, 
	index, 
	onUpdate, 
	onDelete,
	isNew = false,
	campaignSlug
}) => {
	// Calculate initial trigger type based on sequence data
	const getInitialTriggerType = () => {
		if (!sequence || isNew) return 'preset';
		const triggerOptions = getTriggerOptions(campaignSlug, index);
		const matchingPreset = triggerOptions.find(option => 
			option.minutes === convertToMinutes(sequence.trigger_value, sequence.trigger_unit)
		);
		return matchingPreset ? 'preset' : 'custom';
	};

	// Form state
	const [formData, setFormData] = useState({
		title: sequence?.title || '',
		message: sequence?.message || '',
		trigger_value: sequence?.trigger_value || 2,
		trigger_unit: sequence?.trigger_unit || 'MINUTES',
		trigger_type: getInitialTriggerType(),
	});

	// Validation errors
	const [errors, setErrors] = useState({});
	const [touched, setTouched] = useState({});

	const onUpdateRef = useRef(onUpdate);
	const lastSequenceRef = useRef(null);

	// Update the ref when onUpdate changes
	useEffect(() => {
		onUpdateRef.current = onUpdate;
	}, [onUpdate]);

	// Update form data when sequence changes
	useEffect(() => {
		const currentSequenceKey = sequence ? `${sequence.id}-${sequence.title}-${sequence.message}` : 'empty';
		const lastSequenceKey = lastSequenceRef.current;
		
		if (currentSequenceKey !== lastSequenceKey) {
			const triggerOptions = getTriggerOptions(campaignSlug, index);
			const matchingPreset = triggerOptions.find(option => 
				option.minutes === convertToMinutes(sequence?.trigger_value, sequence?.trigger_unit)
			);
			const triggerType = matchingPreset ? 'preset' : 'custom';

			const newFormData = {
				title: sequence?.title || '',
				message: sequence?.message || '',
				trigger_value: sequence?.trigger_value || 2,
				trigger_unit: sequence?.trigger_unit || 'MINUTES',
				trigger_type: triggerType,
			};
			
			setFormData(newFormData);
			setErrors({}); // Clear errors when sequence changes
			setTouched({}); // Clear touched state when sequence changes
			lastSequenceRef.current = currentSequenceKey;
		}
	}, [sequence?.id, sequence?.title, sequence?.message, sequence?.trigger_value, sequence?.trigger_unit, campaignSlug, index]);

	const triggerOptions = useMemo(() => getTriggerOptions(campaignSlug, index), [campaignSlug, index]);

	// Comprehensive validation function
	const validateForm = useCallback((data = formData) => {
		const newErrors = {};
		
		// Title validation
		if (!data.title || !data.title.toString().trim()) {
			newErrors.title = 'Title is required';
		}
		
		// Message validation
		if (!data.message || !data.message.toString().trim()) {
			newErrors.message = 'Message is required';
		}
		
		// Trigger value validation
		const numValue = parseInt(data.trigger_value);
		if (!data.trigger_value || isNaN(numValue) || numValue < 1) {
			newErrors.trigger_value = 'Number must be at least 1';
		}
		
		return newErrors;
	}, [formData]);

	// Validate specific field
	const validateField = useCallback((name, value) => {
		const tempData = { ...formData, [name]: value };
		const allErrors = validateForm(tempData);
		
		setErrors(prevErrors => ({
			...prevErrors,
			[name]: allErrors[name] || undefined
		}));
		
		return !allErrors[name];
	}, [formData, validateForm]);

	// Check if form is valid
	const isFormValid = useCallback(() => {
		const validationErrors = validateForm();
		return Object.keys(validationErrors).length === 0;
	}, [validateForm]);

	// Handle input changes
	const handleInputChange = useCallback((e) => {
		const { name, value } = e.target;
		
		// Update form data
		setFormData(prev => ({
			...prev,
			[name]: value
		}));

		// Mark field as touched
		setTouched(prev => ({
			...prev,
			[name]: true
		}));

		// Validate field immediately
		validateField(name, value);

		// Notify parent with debounce
		setTimeout(() => {
			onUpdateRef.current(index, name, value);
			// Also notify parent about validation status
			const tempData = { ...formData, [name]: value };
			const isValid = Object.keys(validateForm(tempData)).length === 0;
			onUpdateRef.current(index, 'isValid', isValid);
		}, 100);
	}, [index, formData, validateField, validateForm]);

	// Handle blur events to show validation errors
	const handleBlur = useCallback((e) => {
		const { name, value } = e.target;
		
		setTouched(prev => ({
			...prev,
			[name]: true
		}));
		
		validateField(name, value);
	}, [validateField]);

	// Handle preset change
	const handlePresetChange = useCallback((e) => {
		const value = e.target.value;
		const selectedOption = triggerOptions.find(opt => opt.value === value);
		
		if (selectedOption && selectedOption.value !== 'CUSTOM') {
			const { value: triggerValue, unit } = convertFromMinutes(selectedOption.minutes);
			const newFormData = {
				...formData,
				trigger_type: 'preset',
				trigger_value: triggerValue,
				trigger_unit: unit
			};
			
			setFormData(newFormData);
			
			// Validate trigger_value with new value
			validateField('trigger_value', triggerValue);
			
			// Notify parent
			onUpdateRef.current(index, 'trigger_type', 'preset');
			onUpdateRef.current(index, 'trigger_value', triggerValue);
			onUpdateRef.current(index, 'trigger_unit', unit);
			
			// Check overall form validity
			setTimeout(() => {
				const isValid = Object.keys(validateForm(newFormData)).length === 0;
				onUpdateRef.current(index, 'isValid', isValid);
			}, 100);
		} else {
			setFormData(prev => ({
				...prev,
				trigger_type: 'custom'
			}));
			onUpdateRef.current(index, 'trigger_type', 'custom');
		}
	}, [formData, triggerOptions, index, validateField, validateForm]);

	// Handle unit change
	const handleUnitChange = useCallback((e) => {
		const value = e.target.value;
		
		// When unit changes, adjust the value to stay within reasonable range
		const currentMinutes = convertToMinutes(parseInt(formData.trigger_value), formData.trigger_unit);
		let newValue = formData.trigger_value;
		
		// Convert current time to new unit
		switch (value) {
			case 'MINUTES':
				newValue = Math.max(1, currentMinutes);
				break;
			case 'HOURS':
				newValue = Math.max(1, Math.ceil(currentMinutes / 60));
				break;
			case 'DAYS':
				newValue = Math.max(1, Math.ceil(currentMinutes / (24 * 60)));
				break;
		}
		
		const newFormData = {
			...formData,
			trigger_unit: value,
			trigger_value: newValue
		};
		
		setFormData(newFormData);
		
		// Validate the new trigger value
		validateField('trigger_value', newValue);
		
		// Notify parent
		onUpdateRef.current(index, 'trigger_unit', value);
		onUpdateRef.current(index, 'trigger_value', newValue);
		
		// Check overall form validity
		setTimeout(() => {
			const isValid = Object.keys(validateForm(newFormData)).length === 0;
			onUpdateRef.current(index, 'isValid', isValid);
		}, 100);
	}, [formData, index, validateField, validateForm]);

	// Initial validation check
	useEffect(() => {
		const isValid = isFormValid();
		onUpdateRef.current(index, 'isValid', isValid);
	}, [index, isFormValid]);

	// Get current preset value for dropdown
	const getCurrentPresetValue = useMemo(() => {
		if (formData.trigger_type === 'custom') return 'CUSTOM';
		
		const currentMinutes = convertToMinutes(parseInt(formData.trigger_value), formData.trigger_unit);
		const matchingPreset = triggerOptions.find(option => option.minutes === currentMinutes);
		return matchingPreset ? matchingPreset.value : 'CUSTOM';
	}, [formData, triggerOptions]);

	return (
		<div className="border-t border-gray-100">
			<div className="flex flex-row gap-2 items-center mb-4 p-3">
				<span className={clsx(
					"h-5 w-5 text-sm rounded-full items-center justify-center text-white inline-flex",
					isFormValid() ? "bg-green-600" : "bg-red-500"
				)}>
					{index + 1}
				</span>
				<h4 className="text-gray-900 font-semibold text-sm flex-1">
					Campaign {index + 1}
				</h4>
				{(index > 0 || isNew) && (
					<button
						onClick={onDelete}
						className="text-gray-400 hover:text-red-500 text-sm"
					>
						<TrashIcon className="w-5 h-5" />
					</button>
				)}
			</div>
			
			<div className="px-3 pb-3 space-y-4">
				<div className="flex flex-row gap-2">
					<div className="flex-1">
						<label className="block text-sm font-medium text-gray-700">
							Title <span className="text-red-500">*</span>
						</label>
						<input
							name="title"
							type="text"
							value={formData.title}
							onChange={handleInputChange}
							onBlur={handleBlur}
							className={clsx(
								"mt-1 block w-full rounded-md shadow-sm focus:ring-indigo-500 sm:text-sm transition-colors",
								errors.title && touched.title
									? 'border-red-300 focus:border-red-500 bg-red-50' 
									: 'border-gray-300 focus:border-indigo-500'
							)}
							placeholder="e.g., Welcome to our app!"
						/>
						{errors.title && touched.title && (
							<p className="mt-1 text-xs text-red-600 font-medium flex items-center gap-1">
								<svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
									<path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
								</svg>
								{errors.title}
							</p>
						)}
					</div>
					<div className="flex-1">
						<label className="block text-sm font-medium text-gray-700">
							Message <span className="text-red-500">*</span>
						</label>
						<textarea
							name="message"
							rows={3}
							value={formData.message}
							onChange={handleInputChange}
							onBlur={handleBlur}
							className={clsx(
								"mt-1 block w-full rounded-md shadow-sm focus:ring-indigo-500 sm:text-sm transition-colors",
								errors.message && touched.message
									? 'border-red-300 focus:border-red-500 bg-red-50' 
									: 'border-gray-300 focus:border-indigo-500'
							)}
							placeholder="e.g., Thank you for downloading our app! Get started now."
						/>
						{errors.message && touched.message && (
							<p className="mt-1 text-xs text-red-600 font-medium flex items-center gap-1">
								<svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
									<path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
								</svg>
								{errors.message}
							</p>
						)}
					</div>
				</div>
				
				<div>
					<label className="block text-sm font-medium text-gray-700 mb-2">
						Time of Trigger <span className="text-red-500">*</span>
					</label>
					<select
						value={getCurrentPresetValue}
						onChange={handlePresetChange}
						className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
					>
						{triggerOptions.map((option) => (
							<option key={option.value} value={option.value}>
								{option.label}
							</option>
						))}
					</select>
				</div>

				{formData.trigger_type === 'custom' && (
					<div className="flex flex-row gap-2">
						<div className="flex-1">
							<label className="block text-sm font-medium text-gray-700">
								Number <span className="text-red-500">*</span>
							</label>
							<input
								name="trigger_value"
								type="number"
								min="1"
								value={formData.trigger_value}
								onChange={handleInputChange}
								onBlur={handleBlur}
								className={clsx(
									"mt-1 block w-full rounded-md shadow-sm focus:ring-indigo-500 sm:text-sm transition-colors",
									errors.trigger_value && touched.trigger_value
										? 'border-red-300 focus:border-red-500 bg-red-50' 
										: 'border-gray-300 focus:border-indigo-500'
								)}
								placeholder="Enter Number"
							/>
							{errors.trigger_value && touched.trigger_value && (
								<p className="mt-1 text-xs text-red-600 font-medium flex items-center gap-1">
									<svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
										<path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
									</svg>
									{errors.trigger_value}
								</p>
							)}
						</div>
						<div className="flex-1">
							<label className="block text-sm font-medium text-gray-700">
								Unit of Time <span className="text-red-500">*</span>
							</label>
							<select
								value={formData.trigger_unit}
								onChange={handleUnitChange}
								className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
							>
								<option value="MINUTES">Mins</option>
								<option value="HOURS">Hours</option>
								<option value="DAYS">Days</option>
							</select>
						</div>
					</div>
				)}
			</div>
		</div>
	);
};

export default CampaignSequenceForm; 